import { Person, Star } from '@mui/icons-material';
import { Box, Button, Typography } from '@mui/material';
import React, { useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { UserContext } from '@/shared/contexts/UserContext';
import { useMessageService } from '@/shared/services/MessageService/MessageServiceContext';

interface ProfileSetupModalProps {
  onComplete?: () => void;
}

function ProfileSetupModal({ onComplete }: ProfileSetupModalProps) {
  const navigate = useNavigate();
  const { user } = useContext(UserContext);
  const { closeMessage } = useMessageService();
  const [closing, setClosing] = useState(false);

  const hasUsername = !!user?.username;
  const hasTagline = !!user?.tagLine;

  const handleSetupProfile = () => {
    setClosing(true);
    closeMessage();
    navigate('/account-settings?tab=profile');
    if (onComplete) {
      onComplete();
    }
  };

  const handleSkip = () => {
    setClosing(true);
    closeMessage();
    if (onComplete) {
      onComplete();
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 3,
        p: 3,
        textAlign: 'center',
      }}
    >
      {/* Celebration Icon */}
      <Box
        sx={{
          width: 80,
          height: 80,
          borderRadius: '50%',
          background: 'linear-gradient(135deg, #c05a3a 0%, #d4825e 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '0 8px 24px rgba(192, 90, 58, 0.3)',
          mb: 1,
        }}
      >
        <Star sx={{ fontSize: 48, color: 'white' }} />
      </Box>

      {/* Header Text */}
      <Box>
        <Typography
          variant="h5"
          sx={{
            fontWeight: 700,
            color: 'text.primary',
            mb: 1,
          }}
        >
          Congratulations on Your First Review!
        </Typography>
        <Typography
          variant="body1"
          sx={{
            color: 'text.secondary',
            lineHeight: 1.6,
          }}
        >
          You've taken the first step in building your professional reputation on Kandir.
        </Typography>
      </Box>

      {/* Profile Setup Prompt */}
      <Box
        sx={{
          bgcolor: 'rgba(192, 90, 58, 0.08)',
          borderRadius: 3,
          p: 3,
          width: '100%',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <Person sx={{ fontSize: 32, color: 'primary.main' }} />
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              color: 'text.primary',
            }}
          >
            Complete Your Profile
          </Typography>
        </Box>

        <Typography
          variant="body2"
          sx={{
            color: 'text.secondary',
            mb: 2,
            textAlign: 'left',
          }}
        >
          Stand out by setting up your profile:
        </Typography>

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 1.5,
            textAlign: 'left',
            pl: 2,
          }}
        >
          {!hasUsername && (
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
              <Box
                sx={{
                  width: 6,
                  height: 6,
                  borderRadius: '50%',
                  bgcolor: 'primary.main',
                  mt: 1,
                }}
              />
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                <strong>Username:</strong>
                {' '}
                Choose a unique display name that represents you
              </Typography>
            </Box>
          )}

          {!hasTagline && (
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
              <Box
                sx={{
                  width: 6,
                  height: 6,
                  borderRadius: '50%',
                  bgcolor: 'primary.main',
                  mt: 1,
                }}
              />
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                <strong>Tagline:</strong>
                {' '}
                Share your expertise or professional headline
              </Typography>
            </Box>
          )}

          {hasUsername && hasTagline && (
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Add more details to your profile to stand out from the crowd
            </Typography>
          )}
        </Box>
      </Box>

      {/* Action Buttons */}
      <Box
        sx={{
          display: 'flex',
          gap: 2,
          width: '100%',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'center',
        }}
      >
        <Button
          variant="outlined"
          onClick={handleSkip}
          disabled={closing}
          sx={{
            'flex': 1,
            'maxWidth': { sm: 200 },
            'textTransform': 'none',
            'fontWeight': 600,
            'borderColor': 'primary.main',
            'color': 'primary.main',
            '&:hover': {
              borderColor: 'primary.dark',
              backgroundColor: 'rgba(192, 90, 58, 0.04)',
            },
          }}
        >
          Maybe Later
        </Button>
        <Button
          variant="contained"
          onClick={handleSetupProfile}
          disabled={closing}
          sx={{
            'flex': 1,
            'maxWidth': { sm: 200 },
            'textTransform': 'none',
            'fontWeight': 600,
            'background': 'linear-gradient(135deg, #c05a3a 0%, #d4825e 100%)',
            'boxShadow': '0 4px 12px rgba(192, 90, 58, 0.25)',
            '&:hover': {
              boxShadow: '0 6px 16px rgba(192, 90, 58, 0.35)',
              transform: 'translateY(-2px)',
            },
            'transition': 'all 0.2s ease',
          }}
        >
          Set Up Profile
        </Button>
      </Box>
    </Box>
  );
}

export default ProfileSetupModal;
